import { usePaymentLanguage } from '../../lib/paymentLanguage';
import React from 'react';
import { Input } from './input';
import { Label } from './label';
import { CreditCard } from 'lucide-react';

interface CardInputProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    error?: string;
    label?: string;
    id?: string;
}

// Функция форматирования номера карты
const formatCardNumber = (value: string): string => {
    // Удаляем все символы кроме цифр
    const digits = value.replace(/\D/g, '');

    // Ограничиваем до 19 цифр (максимум для некоторых карт)
    const limitedDigits = digits.slice(0, 19);

    // Форматируем группами по 4 цифры
    const formatted = limitedDigits.replace(/(\d{4})(?=\d)/g, '$1 ');

    return formatted;
};

// Функция определения типа карты
const getCardType = (cardNumber: string): string => {
    const digits = cardNumber.replace(/\D/g, '');

    if (digits.startsWith('4')) return 'Visa';
    if (digits.startsWith('5') || (digits.startsWith('2') && parseInt(digits.slice(0, 4)) >= 2221 && parseInt(digits.slice(0, 4)) <= 2720)) return 'MasterCard';
    if (digits.startsWith('3')) return 'American Express';
    if (digits.startsWith('2200') || digits.startsWith('2204')) return 'МИР';

    return '';
};

// Функция валидации номера карты (упрощенная - только длина)
const isValidCardNumber = (cardNumber: string): boolean => {
    const digits = cardNumber.replace(/\D/g, '');
    // Принимаем карты от 13 до 19 цифр (стандартные диапазоны для банковских карт)
    return digits.length >= 13 && digits.length <= 19;
};

export const CardInput: React.FC<CardInputProps> = ({
    value,
    onChange,
    placeholder = "0000 0000 0000 0000",
    disabled = false,
    error,
    label,
    id
}) => {
    const { t } = usePaymentLanguage();
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        const formattedValue = formatCardNumber(inputValue);
        onChange(formattedValue);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Разрешаем навигационные клавиши
        if ([
            'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
            'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
            'Home', 'End'
        ].includes(e.key)) {
            return;
        }

        // Разрешаем Ctrl/Cmd-сочетания независимо от раскладки клавиатуры.
        if ((e.ctrlKey || e.metaKey) && (
            ['KeyA', 'KeyC', 'KeyV', 'KeyX', 'KeyZ', 'KeyY'].includes(e.code) ||
            ['a', 'c', 'v', 'x', 'z', 'y'].includes(e.key.toLowerCase())
        )) {
            return;
        }

        // Разрешаем только цифры
        if (!/^\d$/.test(e.key)) {
            e.preventDefault();
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text');
        const formattedValue = formatCardNumber(pastedText);
        onChange(formattedValue);
    };

    const cardType = getCardType(value);
    const digits = value.replace(/\D/g, '');
    const isValid = digits.length >= 13 && isValidCardNumber(value);

    return (
        <div className="space-y-2">
            {label && <Label htmlFor={id}>{label}</Label>}
            <div className="relative">
                <Input
                    id={id}
                    type="text"
                    value={value}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    placeholder={placeholder}
                    disabled={disabled}
                    maxLength={23} // 19 цифр + 4 пробела
                    className="pl-10" // Место для иконки
                />

                {/* Иконка карты */}
                <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />

                {/* Тип карты */}
                {cardType && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <span className={`text-xs font-medium px-2 py-1 rounded ${isValid ? 'text-green-600 dark:text-green-300 bg-green-50 dark:bg-green-950' : 'text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-950'
                            }`}>
                            {cardType}
                        </span>
                    </div>
                )}
            </div>

            {error && (
                <p className="text-sm text-red-500">{error}</p>
            )}

            {/* Индикатор валидности */}
            {digits.length >= 13 && (
                <p className={`text-xs ${isValid ? 'text-green-600 dark:text-green-300' : 'text-red-500'}`}>
                    {isValid ? t("✓ Номер карты корректен") : t("✗ Некорректный номер карты")}
                </p>
            )}
        </div>
    );
};
